import { useState } from 'react';
import { Table, Button, Row, Col, Card, Form, Accordion, Badge } from 'react-bootstrap';
import { FaEye, FaTimes, FaSearch, FaFilter, FaClipboardList } from 'react-icons/fa';
import { LinkContainer } from 'react-router-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Paginate from '../../components/ui/Paginate';
import Meta from '../../components/layout/Meta';
import { useGetOrdersQuery } from '../../slices/orderApiSlice';
import useIsMobile from '../../utils/useIsMobile';

const OrderListScreen = () => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { pageNumber, keyword: urlKeyword } = useParams();

    const [keyword, setKeyword] = useState(urlKeyword || '');
    const [isPaid, setIsPaid] = useState('');
    const [isDelivered, setIsDelivered] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const {
        data,
        isLoading: loadingOrders,
        isFetching,
        error,
    } = useGetOrdersQuery({
        pageNumber: pageNumber || 1,
        keyword: urlKeyword || '',
        isPaid,
        isDelivered,
        paymentMethod,
        startDate,
        endDate,
    });

    const orders = data?.orders || data?.results || (Array.isArray(data) ? data : []);

    const submitHandler = (e) => {
        e.preventDefault();
        if (keyword.trim()) {
            navigate(`/admin/orderlist/search/${keyword.trim()}`);
        } else {
            navigate('/admin/orderlist');
        }
    };

    const clearFilters = () => {
        setKeyword('');
        setIsPaid('');
        setIsDelivered('');
        setPaymentMethod('');
        setStartDate('');
        setEndDate('');
        navigate('/admin/orderlist');
    };

    return (
        <>
            <Meta title="Lista de Pedidos" />
            <div className="admin-header">
                <h1 className="admin-title responsive-title">
                    <FaClipboardList className="title-icon" /> Pedidos
                    {data?.total && (
                        <Badge bg="secondary" className="ms-2">
                            {data.total}
                        </Badge>
                    )}
                </h1>
            </div>

            <Form onSubmit={submitHandler} className="mb-4">
                <Row className="g-2 align-items-center">
                    <Col md={8}>
                        <Form.Control
                            type="text"
                            placeholder="Buscar por ID o Nombre de Usuario..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                        />
                    </Col>
                    <Col md={4} className="d-flex gap-2">
                        <Button type="submit" variant="outline-primary" className="flex-grow-1">
                            <FaSearch /> Buscar
                        </Button>
                        <Button variant="outline-secondary" onClick={clearFilters} className="flex-grow-1">
                            Limpiar
                        </Button>
                    </Col>
                </Row>

                <Accordion className="mt-2">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <FaFilter className="me-2" /> Filtros Avanzados
                        </Accordion.Header>
                        <Accordion.Body>
                            <Row className="g-2">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label column='sm' className="small mb-1">Estado Pago</Form.Label>
                                        <Form.Select value={isPaid} onChange={(e) => setIsPaid(e.target.value)}>
                                            <option value="">Todos</option>
                                            <option value="true">Pagado</option>
                                            <option value="false">No Pagado</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label column='sm' className="small mb-1">Estado Entrega</Form.Label>
                                        <Form.Select
                                            value={isDelivered}
                                            onChange={(e) => setIsDelivered(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="true">Entregado</option>
                                            <option value="false">No Entregado</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label column='sm' className="small mb-1">{'Método Pago'}</Form.Label>
                                        <Form.Select
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="Transfer">Transferencia</option>
                                            <option value="PayPhone">PayPhone</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="g-2 mt-2">
                                <Col md={6}>
                                    <Form.Group controlId="startDate">
                                        <Form.Label column='sm' className="small mb-1">Fecha Inicio</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="endDate">
                                        <Form.Label column='sm' className="small mb-1">Fecha Fin</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Form>

            {loadingOrders || isFetching ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">
                    {error?.data?.message || error?.error || 'Ocurrió un error inesperado'}
                </Message>
            ) : (
                <>
                    {/* View for Desktop */}
                    {!isMobile && (
                        <div className="admin-table-container shadow-sm mb-4">
                            <Table hover responsive className="admin-table m-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th className="text-start">USUARIO</th>
                                        <th>FECHA</th>
                                        <th>TOTAL</th>
                                        <th>PAGADO</th>
                                        <th>ENTREGADO</th>
                                        <th className="text-center">ACCIONES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders?.map((order) => (
                                        <tr key={order._id}>
                                            <td><Badge bg="light" text="dark">{order._id.substring(order._id.length - 8)}</Badge></td>
                                            <td className="text-start fw-bold">{order.user && order.user.name}</td>
                                            <td>{order.createdAt?.substring(0, 10)}</td>
                                            <td className="fw-bold">${order.totalPrice.toFixed(2)}</td>
                                            <td>
                                                {order.isPaid ? (
                                                    <Badge bg="success" className="status-badge">
                                                        {order.paidAt?.substring(0, 10)}
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="danger" className="status-badge">
                                                        <FaTimes /> No
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {order.isDelivered ? (
                                                    <Badge bg="success" className="status-badge">
                                                        {order.deliveredAt?.substring(0, 10)}
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="warning" text="dark" className="status-badge">
                                                        <FaTimes /> No
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="action-buttons justify-content-center">
                                                    <LinkContainer to={`/order/${order._id}`}>
                                                        <Button variant="outline-primary" className="btn-sm" title="Ver Detalles">
                                                            <FaEye />
                                                        </Button>
                                                    </LinkContainer>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* View for Mobile */}
                    {isMobile && (
                        <div className="d-md-none">
                            {orders?.map((order) => (
                                <Card key={order._id} className="mb-3 shadow-sm">
                                    <Card.Body>
                                        <Row className="align-items-center mb-2">
                                            <Col>
                                                <strong>ID:</strong> {order._id}
                                            </Col>
                                            <Col className="text-end">
                                                <LinkContainer to={`/order/${order._id}`}>
                                                    <Button variant="light" className="btn-sm">
                                                        <FaEye /> Detalle
                                                    </Button>
                                                </LinkContainer>
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col>
                                                <strong>Usuario:</strong> {order.user && order.user.name}
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col>
                                                <strong>Fecha:</strong> {order.createdAt?.substring(0, 10)}
                                            </Col>
                                            <Col className="text-end">
                                                <strong>Total:</strong> ${order.totalPrice}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <strong>Pagado:</strong>{' '}
                                                {order.isPaid ? (
                                                    <span className="text-success">{order.paidAt?.substring(0, 10)}</span>
                                                ) : (
                                                    <FaTimes style={{ color: 'red' }} />
                                                )}
                                            </Col>
                                            <Col>
                                                <strong>Entregado:</strong>{' '}
                                                {order.isDelivered ? (
                                                    <span className="text-success">{order.deliveredAt?.substring(0, 10)}</span>
                                                ) : (
                                                    <FaTimes style={{ color: 'red' }} />
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                    <Paginate
                        pages={data?.pages}
                        page={data?.page}
                        isAdmin="orders"
                        keyword={urlKeyword ? urlKeyword : ''}
                        scrollTargetId="admin-orders-section"
                    />
                </>
            )}
        </>
    );
};

export default OrderListScreen;
